import { Injectable } from '@angular/core';

/**
 * Estado en memoria del fallo de backend que originó la redirección a /503.
 * Se mantiene en memoria (no sessionStorage): el flujo completo (fallo -> 503
 * -> reintentos -> recuperación) ocurre en una sola sesión SPA sin recargas.
 */
@Injectable({
  providedIn: 'root',
})
export class ServiceUnavailableStateService {
  private _failedPath: string | null = null; // path interno (pathname+search) para navigateByUrl
  private _failedUrl: string | null = null;  // URL completa (con origin) para mostrar
  private _attempts = 0;                     // reintentos ya realizados
  private resetTimer: ReturnType<typeof setTimeout> | null = null;

  get failedPath(): string | null {
    return this._failedPath;
  }

  get failedUrl(): string | null {
    return this._failedUrl;
  }

  get attempts(): number {
    return this._attempts;
  }

  /**
   * Registra un fallo de backend.
   * - path /503: no-op (fallo de la propia página 503).
   * - path distinto al guardado: reinicia attempts y guarda el nuevo.
   * - mismo path: conserva attempts (continúa el ciclo de reintentos).
   * Cancela cualquier reset de éxito pendiente.
   */
  registerFailure(failedPath: string, failedUrl: string): void {
    if (failedPath.endsWith('/503')) {
      return;
    }
    this.cancelResetTimer();
    if (this._failedPath !== failedPath) {
      this._failedPath = failedPath;
      this._failedUrl = failedUrl;
      this._attempts = 0;
    } else if (this._failedUrl !== failedUrl) {
      this._failedUrl = failedUrl;
    }
  }

  /** true mientras queden reintentos (máx. 3). */
  canRetry(): boolean {
    return this._failedPath !== null && this._attempts < 3;
  }

  incrementAttempts(): void {
    this._attempts += 1;
  }

  /** Reset diferido tras recuperación; un nuevo fallo lo cancela. */
  scheduleSuccessReset(): void {
    this.cancelResetTimer();
    this.resetTimer = setTimeout(() => this.reset(), 15000);
  }

  reset(): void {
    this.cancelResetTimer();
    this._failedPath = null;
    this._failedUrl = null;
    this._attempts = 0;
  }

  private cancelResetTimer(): void {
    if (this.resetTimer !== null) {
      clearTimeout(this.resetTimer);
      this.resetTimer = null;
    }
  }
}
